<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';

const props = defineProps({
  description: {
    type: String,
    default: '',
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save']);

let saveTimeout = null;
const lastEmittedContent = ref('');

const descriptionEditor = useEditor({
  content: props.description || '<p>Click to add a description...</p>',
  editable: true,
  extensions: [StarterKit],
  editorProps: {
    attributes: {
      class:
        'prose prose-sm max-w-none min-h-[120px] p-3 focus:outline-none dark:prose-invert !pr-10',
    },
  },
  onUpdate: ({ editor: editorInstance }) => {
    const html = editorInstance.getHTML();

    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Debounce the save
    saveTimeout = setTimeout(() => {
      lastEmittedContent.value = html;
      emit('save', html);
    }, 500);
  },
});

watch(
  () => props.description,
  newValue => {
    // Don't update if this is the same content we just emitted
    if (newValue === lastEmittedContent.value) return;

    // Don't update if editor already has this content
    const currentContent = descriptionEditor.value?.getHTML();
    if (currentContent === newValue) return;

    // Only update from external changes
    if (descriptionEditor.value) {
      descriptionEditor.value.commands.setContent(
        newValue || '<p>Click to add a description...</p>',
        false
      );
      lastEmittedContent.value = newValue;
    }
  }
);

onBeforeUnmount(() => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  if (descriptionEditor.value) {
    descriptionEditor.value.destroy();
  }
});

const focusEditor = () => {
  if (descriptionEditor.value) {
    descriptionEditor.value.commands.focus('end');
  }
};
</script>

<template>
  <div class="relative">
    <button
      class="absolute flex top-0 right-0 z-10 p-2 text-slate-400 hover:text-purple-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors w-10 h-10 justify-center items-center"
      @click="focusEditor"
    >
      <Icon icon="i-lucide-pen-line" class="size-4" />
    </button>

    <EditorContent :editor="descriptionEditor" />
  </div>
</template>
